import React, { useState } from 'react';
import { Drawer, List, Typography } from 'antd';

const { Text } = Typography;

export interface ActionSheetItem {
  key: string;
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  description?: string;
}

export interface MobileActionSheetProps {
  /** Trigger element (usually a button or icon) */
  trigger: React.ReactNode;
  /** List of actions */
  actions: ActionSheetItem[];
  /** Title of the action sheet */
  title?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Whether to close on action click (default: true) */
  closeOnAction?: boolean;
  /** Custom placement */
  placement?: 'bottom' | 'top';
  /** Custom height */
  height?: number | string;
}

/**
 * MobileActionSheet - Bottom sheet with action options
 *
 * Usage:
 * ```tsx
 * <MobileActionSheet
 *   trigger={<Button icon={<MoreOutlined />} />}
 *   title="Actions"
 *   actions={[
 *     { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: handleEdit },
 *     { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', onClick: handleDelete, danger: true },
 *   ]}
 * />
 * ```
 */
const MobileActionSheet: React.FC<MobileActionSheetProps> = ({
  trigger,
  actions,
  title,
  cancelText = 'Cancel',
  closeOnAction = true,
  placement = 'bottom',
  height,
}) => {
  const [visible, setVisible] = useState(false);

  const handleActionClick = (action: ActionSheetItem) => {
    if (action.disabled) return;

    action.onClick();

    if (closeOnAction) {
      setVisible(false);
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(true);
  };

  return (
    <>
      <span onClick={handleTriggerClick} style={{ cursor: 'pointer' }}>
        {trigger}
      </span>

      <Drawer
        title={title}
        placement={placement}
        open={visible}
        onClose={() => setVisible(false)}
        height={height || 'auto'}
        styles={{
          body: { padding: 0 },
          header: { borderBottom: '1px solid #f0f0f0' },
        }}
        closeIcon={null}
      >
        <List
          dataSource={actions}
          renderItem={(action) => (
            <List.Item
              onClick={() => handleActionClick(action)}
              style={{
                padding: '16px 24px',
                cursor: action.disabled ? 'not-allowed' : 'pointer',
                opacity: action.disabled ? 0.5 : 1,
                backgroundColor: action.danger ? '#fff2f0' : undefined,
              }}
              className="mobile-action-sheet-item"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                {action.icon && (
                  <span
                    style={{
                      fontSize: 20,
                      color: action.danger ? '#ff4d4f' : '#1890ff',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {action.icon}
                  </span>
                )}
                <div style={{ flex: 1 }}>
                  <Text
                    strong
                    style={{
                      color: action.danger ? '#ff4d4f' : undefined,
                      fontSize: 16,
                    }}
                  >
                    {action.label}
                  </Text>
                  {action.description && (
                    <Text
                      type="secondary"
                      style={{
                        display: 'block',
                        fontSize: 13,
                        marginTop: 2,
                      }}
                    >
                      {action.description}
                    </Text>
                  )}
                </div>
              </div>
            </List.Item>
          )}
        />

        {/* Cancel button */}
        <div
          style={{
            borderTop: '8px solid #f5f5f5',
          }}
        >
          <List.Item
            onClick={() => setVisible(false)}
            style={{
              padding: '16px 24px',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            <Text style={{ width: '100%', textAlign: 'center', fontSize: 16 }}>
              {cancelText}
            </Text>
          </List.Item>
        </div>
      </Drawer>
    </>
  );
};

export default MobileActionSheet;
